import React, { useState, useRef } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  CircularProgress, 
  Grid, 
  Paper, 
  IconButton,
  TextField,
  LinearProgress,
  Alert,
  Stack,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Photo as PhotoIcon,
  Image as CoverPhotoIcon,
  LocationOn as LocationIcon,
  CalendarToday as DateIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, getStorage } from 'firebase/storage';
import {
   collection,
   addDoc,
   updateDoc,
   serverTimestamp,
   doc,
   arrayUnion,
   getDoc,
   arrayRemove,
   Timestamp,
} from 'firebase/firestore';
import { db, storage } from '../../firebase/config';
import { useUser } from '../../contexts/UserContext';
import GooglePlacesSearch from '../common/GooglePlacesSearch';
import { getImageTakenDate } from '../../utils/imageUtils';
import { preparePhotosForUpload } from '../../utils/preparePhotosForUpload';

interface EventPhotoUploaderProps {
   eventId: string;
   onUploadComplete: () => void;
   eventLocation: {
      name: string;
      address: string;
      coordinates: [number, number];
   } | null | undefined;
}

interface UploadingPhoto {
   file: File;
   preview: string;
   progress: number;
   title: string;
   description: string;
   error?: string;
   setAsCover?: boolean;
   location?: {
      name: string;
      address?: string;
      coordinates: {
         latitude: number;
         longitude: number;
      };
   } | undefined;
   takenAt?: Date | null;
   id?: string;
   url?: string;
}

// Function to extract EXIF data from an image file (simplified)
const extractExifData = (
   file: File
): Promise<{
   dateTaken?: Date | null;
   location?: { name: string; coordinates: { latitude: number; longitude: number } } | null;
}> => {
   return new Promise((resolve) => {
      // Default result with no EXIF data
      const result: {
         dateTaken?: Date | null;
         location?: { name: string; coordinates: { latitude: number; longitude: number } } | null;
      } = {
         dateTaken: new Date(file.lastModified),
         location: null,
      };

      resolve(result);
   });
};

const EventPhotoUploader: React.FC<EventPhotoUploaderProps> = ({
   eventId,
   onUploadComplete,
   eventLocation = null,
}) => {
   const { currentUser } = useUser();
   const [uploadingPhotos, setUploadingPhotos] = useState<UploadingPhoto[]>([]);
   const [isUploading, setIsUploading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [successMessage, setSuccessMessage] = useState<string | null>(null);
   const fileInputRef = useRef<HTMLInputElement>(null);
   const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
   const [photoToDelete, setPhotoToDelete] = useState<UploadingPhoto | null>(null);

   // Check if user can upload photos (all event members can upload: owner, editor, viewer)
   const checkPermission = async (): Promise<boolean> => {
      if (!currentUser) return false;

      try {
         const eventDoc = await getDoc(doc(db, 'events', eventId));
         if (!eventDoc.exists()) return false;

         const eventData = eventDoc.data();

         // Allow if user is the event owner (legacy check)
         if (eventData.userId === currentUser.uid) return true;

         // Check if user is a member in the new members array structure
         const members = eventData.members || [];
         const isMember = members.some((member: any) => member.userId === currentUser.uid);
         if (isMember) return true;

         // Fallback: Check if user is an accepted participant (legacy structure)
         const participants = eventData.participants || [];
         return participants.some(
            (p: any) => p.userId === currentUser.uid && p.status === 'accepted'
         );
      } catch (err) {
         console.error('Error checking permissions:', err);
         return false;
      }
   };

   const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length === 0) return;

      const selectedFiles = Array.from(event.target.files);
      const newPhotos: UploadingPhoto[] = [...uploadingPhotos];

      // Check if we already have a cover photo in the existing uploaded photos
      const hasCoverPhoto = uploadingPhotos.some((photo) => photo.setAsCover);

      let filesProcessed = 0;
      const totalFiles = selectedFiles.length;

      selectedFiles.forEach((file) => {
         // Check if file is an image
         if (!file.type.startsWith('image/')) {
            setError(`${file.name} is not an image file`);
            return;
         }

         // Create a preview
         const reader = new FileReader();
         reader.onload = async (e) => {
            if (e.target && e.target.result) {
               const metadata = await getImageTakenDate(file);
               const newPhoto: UploadingPhoto = {
                  file,
                  preview: e.target.result as string,
                  progress: 0,
                  title: file.name.split('.')[0], // Default title from filename
                  description: '',
                  // Set as cover only if this is the first photo ever and no cover exists yet
                  setAsCover:
                     !hasCoverPhoto && newPhotos.length === 0 && uploadingPhotos.length === 0,
                  takenAt: metadata.takenDate
                     ? Timestamp.fromDate(metadata.takenDate).toDate()
                     : null,
                  location: eventLocation ? {
                     name: eventLocation.name || '',
                     address: eventLocation.address || '',
                     coordinates: {
                        latitude: eventLocation.coordinates?.[0] || 0,
                        longitude: eventLocation.coordinates?.[1] || 0,
                     },
                  } : undefined,
               };

               // Extract EXIF data
               // extractExifData(file).then((exifData) => {
               //    if (exifData.dateTaken) {
               //       newPhoto.takenAt = exifData.dateTaken;
               //    }

               //    if (
               //       exifData.location &&
               //       exifData.location.coordinates.latitude &&
               //       exifData.location.coordinates.longitude
               //    ) {
               //       newPhoto.location = exifData.location;
               //    }

               // });

               newPhotos.push(newPhoto);
               filesProcessed++;

               // Only update state when all files are processed
               if (filesProcessed === totalFiles) {
                  setUploadingPhotos([...newPhotos]);
               }
            }
         };
         reader.readAsDataURL(file);
      });

      // Reset file input
      if (fileInputRef.current) {
         fileInputRef.current.value = '';
      }
   };

   const handleRemovePhoto = (index: number) => {
      const newPhotos = [...uploadingPhotos];

      // If removing a cover photo, assign another one if available
      if (newPhotos[index].setAsCover && newPhotos.length > 1) {
         const nextIndex = index === 0 ? 1 : 0;
         newPhotos[nextIndex].setAsCover = true;
      }

      newPhotos.splice(index, 1);
      setUploadingPhotos(newPhotos);
   };

   const handlePhotoTitleChange = (index: number, value: string) => {
      const newPhotos = [...uploadingPhotos];
      newPhotos[index].title = value;
      setUploadingPhotos(newPhotos);
   };

   const handlePhotoDescriptionChange = (index: number, value: string) => {
      const newPhotos = [...uploadingPhotos];
      newPhotos[index].description = value;
      setUploadingPhotos(newPhotos);
   };

   const handlePhotoLocationChange = (
      index: number,
      value: { name: string; coordinates: { latitude: number; longitude: number } }
   ) => {
      const newPhotos = [...uploadingPhotos];
      newPhotos[index].location = value;
      setUploadingPhotos(newPhotos);
   };

   const handlePhotoDateChange = (index: number, value: Date | null) => {
      const newPhotos = [...uploadingPhotos];
      newPhotos[index].takenAt = value;
      setUploadingPhotos(newPhotos);
   };

   const handleSetAsCover = (index: number) => {
      // Create a new copy of the photos array
      const newPhotos = [...uploadingPhotos];

      // First, unset all photos as cover
      newPhotos.forEach((photo) => {
         photo.setAsCover = false;
      });

      // Then set only the selected photo as cover
      newPhotos[index].setAsCover = true;

      // Apply the changes
      setUploadingPhotos(newPhotos);

      // Show feedback
      setSuccessMessage(`Set "${newPhotos[index].title}" as cover photo`);
      setTimeout(() => setSuccessMessage(null), 3000);
   };

   const handleDeletePhoto = (photo: UploadingPhoto) => {
      setPhotoToDelete(photo);
      setOpenDeleteDialog(true);
   };

   const handleDeleteCancel = () => {
      setOpenDeleteDialog(false);
      setPhotoToDelete(null);
   };

   const handleDeleteConfirm = async () => {
      if (!photoToDelete || !photoToDelete.id || !photoToDelete.url) {
         setError('Cannot delete photo: Missing photo information');
         return;
      }

      try {
         // Get reference to the event document
         const eventRef = doc(db, 'events', eventId);
         const eventDoc = await getDoc(eventRef);

         if (!eventDoc.exists()) {
            throw new Error('Event not found');
         }

         // Remove the photo from Firestore
         await updateDoc(eventRef, {
            photos: arrayRemove({
               id: photoToDelete.id,
               url: photoToDelete.url,
               title: photoToDelete.title,
               description: photoToDelete.description,
               location: photoToDelete.location,
               takenAt: photoToDelete.takenAt,
               uploadedBy: currentUser?.uid,
            }),
         });

         // Delete from Firebase Storage if it's stored there
         if (photoToDelete.url.includes('firebase')) {
            const storage = getStorage();
            const photoRef = ref(storage, photoToDelete.url);
            await deleteObject(photoRef);
         }

         setSuccessMessage('Photo deleted successfully');
         onUploadComplete(); // Refresh the photos list
      } catch (err) {
         console.error('Error deleting photo:', err);
         setError('Failed to delete photo. Please try again.');
      } finally {
         setOpenDeleteDialog(false);
         setPhotoToDelete(null);
      }
   };

   const uploadPhotos = async () => {
      try {
         if (!currentUser) {
            setError('You must be logged in to upload photos');
            return;
         }

         // Check if user has permission to upload
         // const hasPermission = await checkPermission();
         // if (!hasPermission) {
         //    setError('You do not have permission to upload photos to this event');
         //    return;
         // }

         if (uploadingPhotos.length === 0) {
            setError('Please select at least one photo to upload');
            return;
         }

         setIsUploading(true);
         setError(null);
         setSuccessMessage(null);

         const { preparedByIndex, errors: prepareErrors } = await preparePhotosForUpload(
            uploadingPhotos.map((p) => p.file)
         );
         if (prepareErrors.length > 0) {
            setSuccessMessage(
               `${prepareErrors.length} file(s) could not be used: ${prepareErrors[0].error}${prepareErrors.length > 1 ? ' …' : ''}`
            );
         }
         const toUpload = uploadingPhotos
            .map((photo, i) => (preparedByIndex[i] ? { photo, file: preparedByIndex[i]! } : null))
            .filter((x): x is { photo: typeof uploadingPhotos[0]; file: File } => x != null);
         if (toUpload.length === 0) {
            setError('No photos could be prepared for upload. Please try different files.');
            setIsUploading(false);
            return;
         }

         let uploadedCount = 0;
         let failedCount = uploadingPhotos.length - toUpload.length;
         const totalCount = uploadingPhotos.length;
         const successfulUploads: any[] = [];
         let coverPhotoUrl: string | null = null;

         for (let idx = 0; idx < toUpload.length; idx++) {
            const { photo, file: preparedFile } = toUpload[idx];
            const i = uploadingPhotos.indexOf(photo);
            const metadata = await getImageTakenDate(preparedFile);

            try {
               const safeFileName = preparedFile.name.replace(/[^a-zA-Z0-9.]/g, '_');
               const fileName = `events/${eventId}/photos/${Date.now()}_${safeFileName}`;
               const storageRef = ref(storage, fileName);

               // Upload the prepared file
               const uploadTask = uploadBytesResumable(storageRef, preparedFile);

               await new Promise<void>((resolve, reject) => {
                  uploadTask.on(
                     'state_changed',
                     (snapshot) => {
                        // Update progress

                        const progress = Math.round(
                           (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                        );
                        const newPhotos = [...uploadingPhotos];
                        newPhotos[i].progress = progress;
                        setUploadingPhotos(newPhotos);
                     },
                     (error) => {
                        // Handle upload error

                        const newPhotos = [...uploadingPhotos];
                        newPhotos[i].error = `Upload failed: ${error.message}`;
                        setUploadingPhotos(newPhotos);
                        failedCount++;
                        resolve(); // Continue with next photo even if this one failed
                     },
                     async () => {
                        // Upload completed successfully
                        try {
                           const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

                           const thumbnailUrl = downloadURL;

                           if (photo.setAsCover) {
                              coverPhotoUrl = downloadURL;
                           }

                           // Create photo document in Firestore
                           const photoData = {
                              url: downloadURL,
                              thumbnailUrl: thumbnailUrl,
                              title: photo.title || 'Untitled Photo',
                              description: photo.description || '',
                              fileName: fileName,
                              size: preparedFile.size,
                              mimeType: preparedFile.type,
                              eventId: eventId,
                              uploadedBy: currentUser.uid,
                              uploadedAt: serverTimestamp(),
                              createdAt: photo.takenAt
                                 ? Timestamp.fromDate(photo.takenAt)
                                 : serverTimestamp(),
                              takenAt: photo.takenAt
                                 ? Timestamp.fromDate(photo.takenAt)
                                 : serverTimestamp(),
                              isCoverPhoto: photo.setAsCover || false,
                              userId: currentUser.uid,
                              tags: [],
                              likeCount: 0,
                              likedBy: [],
                              isEnhanced: false,
                              isTransformed: false,
                              location: photo.location,
                           };

                           try {
                              // Store in photos collection
                              const photoRef = await addDoc(collection(db, 'photos'), photoData);

                              successfulUploads.push({
                                 id: photoRef.id,
                                 ...photoData,
                              });

                              uploadedCount++;
                           } catch (firestoreErr: any) {
                              const newPhotos = [...uploadingPhotos];
                              newPhotos[i].error = `Database error: ${firestoreErr.message}`;
                              setUploadingPhotos(newPhotos);
                              failedCount++;
                           }

                           resolve();
                        } catch (err) {
                           console.error('Error saving photo data:', err);
                           failedCount++;
                           reject(err);
                        }
                     }
                  );
               });
            } catch (err: any) {
               failedCount++;
               // Continue with next photo even if this one failed
            }
         }

         // If a cover photo was uploaded, update the event with the cover photo URL
         if (coverPhotoUrl) {
            try {
               const eventRef = doc(db, 'events', eventId);
               await updateDoc(eventRef, {
                  coverPhotoUrl: coverPhotoUrl,
                  updatedAt: serverTimestamp(),
               });
            } catch (err) {
               console.error('❌ Error updating event:', err);
            }
         }

         setIsUploading(false);

         if (uploadedCount === 0) {
            setError('Failed to upload photos. Please try again.');
         } else if (failedCount > 0) {
            setSuccessMessage(
               `Successfully uploaded ${uploadedCount} out of ${totalCount} photos. ${failedCount} failed.`
            );
            // Only clear successful uploads
            setUploadingPhotos((prev) => prev.filter((photo) => photo.error));
         } else {
            setSuccessMessage(`Successfully uploaded ${uploadedCount} photos.`);
            setUploadingPhotos([]);
            onUploadComplete();
         }
      } catch (error) {
         setError('Failed to upload photos. Please try again.');
      }
   };

   const formatDate = (date: any) => {
      try {
         return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
         });
      } catch (error) {
         return null;
      }
   };

   return (
      <Box>
         <Box sx={{ mb: 2 }}>
            <Typography variant='h6' gutterBottom>
               Upload Photos
            </Typography>
            <Typography variant='body2' color='text.secondary'>
               Select photos to add to this event. The first photo will be used as the cover image
               by default.
            </Typography>
         </Box>

         <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item>
               <input
                  type='file'
                  accept='image/*'
                  multiple
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileSelect}
               />
               <Button
                  variant='outlined'
                  startIcon={<AddIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
               >
                  Select Photos
               </Button>
            </Grid>
            <Grid item>
               <Button
                  variant='contained'
                  color='primary'
                  startIcon={
                     isUploading ? <CircularProgress size={20} color='inherit' /> : <UploadIcon />
                  }
                  onClick={uploadPhotos}
                  disabled={isUploading || uploadingPhotos.length === 0}
               >
                  {isUploading ? 'Uploading...' : 'Upload All Photos'}
               </Button>
            </Grid>
         </Grid>

         {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
               {error}
            </Alert>
         )}

         {successMessage && (
            <Alert severity='success' sx={{ mb: 2 }}>
               {successMessage}
            </Alert>
         )}

         <Grid container spacing={2}>
            {uploadingPhotos.map((photo, index) => (
               <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper sx={{ p: 2, position: 'relative' }}>
                     {/* Image thumbnail with cover photo badge */}
                     <Box sx={{ position: 'relative', mb: 2 }}>
                        <Box
                           sx={{
                              height: 200,
                              width: '100%',
                              backgroundImage: `url(${photo.preview})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              borderRadius: 1,
                              border: photo.setAsCover ? '3px solid #1976d2' : '1px solid #e0e0e0',
                              boxShadow: photo.setAsCover
                                 ? '0 0 10px rgba(25, 118, 210, 0.5)'
                                 : 'none',
                           }}
                        />

                        {/* Cover Photo badge and close button */}
                        <Box
                           sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              display: 'flex',
                              justifyContent: 'space-between',
                              p: 1,
                           }}
                        >
                           {photo.setAsCover && (
                              <Chip
                                 label='Cover Photo'
                                 color='primary'
                                 size='small'
                                 icon={<CoverPhotoIcon />}
                                 sx={{
                                    backgroundColor: 'rgba(25, 118, 210, 0.8)',
                                    fontWeight: 'bold',
                                    backdropFilter: 'blur(4px)',
                                 }}
                              />
                           )}

                           <Box
                              sx={{
                                 position: 'absolute',
                                 top: 8,
                                 right: 8,
                                 display: 'flex',
                                 gap: 1,
                              }}
                           >
                              <IconButton
                                 size='small'
                                 onClick={() => handleRemovePhoto(index)}
                                 sx={{
                                    bgcolor: 'background.paper',
                                    '&:hover': {
                                       bgcolor: 'error.light',
                                       color: 'error.contrastText',
                                    },
                                 }}
                              >
                                 <CloseIcon />
                              </IconButton>
                              {photo.id && (
                                 <IconButton
                                    size='small'
                                    onClick={() => handleDeletePhoto(photo)}
                                    sx={{
                                       bgcolor: 'background.paper',
                                       '&:hover': {
                                          bgcolor: 'error.light',
                                          color: 'error.contrastText',
                                       },
                                    }}
                                 >
                                    <DeleteIcon />
                                 </IconButton>
                              )}
                           </Box>
                        </Box>

                        {/* Cover photo button at the bottom of the image */}
                        <Button
                           variant={photo.setAsCover ? 'contained' : 'outlined'}
                           size='small'
                           startIcon={<CoverPhotoIcon />}
                           onClick={() => handleSetAsCover(index)}
                           disabled={isUploading}
                           fullWidth
                           sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              borderTopLeftRadius: 0,
                              borderTopRightRadius: 0,
                              backgroundColor: photo.setAsCover
                                 ? 'rgba(25, 118, 210, 0.9)'
                                 : 'rgba(255, 255, 255, 0.8)',
                              color: photo.setAsCover ? 'white' : 'rgba(0, 0, 0, 0.7)',
                              fontWeight: photo.setAsCover ? 'bold' : 'normal',
                              backdropFilter: 'blur(4px)',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                 backgroundColor: photo.setAsCover
                                    ? 'rgba(25, 118, 210, 1)'
                                    : 'rgba(255, 255, 255, 0.9)',
                                 color: photo.setAsCover ? 'white' : 'rgba(0, 0, 0, 0.9)',
                              },
                           }}
                        >
                           {photo.setAsCover ? 'Cover Photo ✓' : 'Set as Cover'}
                        </Button>
                     </Box>

                     <TextField
                        fullWidth
                        label='Title'
                        variant='outlined'
                        size='small'
                        value={photo.title}
                        onChange={(e) => handlePhotoTitleChange(index, e.target.value)}
                        disabled={isUploading}
                        sx={{ mb: 2 }}
                     />

                     <TextField
                        fullWidth
                        label='Description'
                        variant='outlined'
                        size='small'
                        multiline
                        rows={2}
                        value={photo.description}
                        onChange={(e) => handlePhotoDescriptionChange(index, e.target.value)}
                        disabled={isUploading}
                        sx={{ mb: 2 }}
                     />
                     <GooglePlacesSearch
                        sx={{ mb: 2 }}
                        value={photo.location?.name || ''}
                        searchType='places'
                        label='Location'
                        onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                           handlePhotoLocationChange(index, {
                              name: place_name,
                              coordinates: {
                                 latitude: lat,
                                 longitude: lng,
                              },
                           });
                        }}
                     />

                     <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DateTimePicker
                           label='Date Taken'
                           value={formatDate(photo.takenAt || '')}
                           onChange={(date) => handlePhotoDateChange(index, date as Date | null)}
                           renderInput={(params) => (
                              <TextField
                                 {...params}
                                 fullWidth
                                 variant='outlined'
                                 size='small'
                                 sx={{ mb: 2 }}
                                 disabled={isUploading}
                                 InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                       <InputAdornment position='start'>
                                          <DateIcon fontSize='small' />
                                       </InputAdornment>
                                    ),
                                 }}
                              />
                           )}
                        />
                     </LocalizationProvider>

                     {isUploading && (
                        <>
                           <LinearProgress
                              variant='determinate'
                              value={photo.progress}
                              sx={{ mt: 1, height: 6, borderRadius: 3 }}
                           />
                           <Typography
                              variant='caption'
                              color='text.secondary'
                              sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}
                           >
                              {photo.progress}% Uploaded
                           </Typography>
                        </>
                     )}

                     {photo.error && (
                        <Typography
                           color='error'
                           variant='caption'
                           sx={{ display: 'block', mt: 1 }}
                        >
                           {photo.error}
                        </Typography>
                     )}
                  </Paper>
               </Grid>
            ))}
         </Grid>

         <Dialog
            open={openDeleteDialog}
            onClose={handleDeleteCancel}
            aria-labelledby='delete-dialog-title'
            aria-describedby='delete-dialog-description'
         >
            <DialogTitle id='delete-dialog-title'>Delete Photo?</DialogTitle>
            <DialogContent>
               <Typography id='delete-dialog-description'>
                  Are you sure you want to delete this photo? This action cannot be undone.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleDeleteCancel}>Cancel</Button>
               <Button onClick={handleDeleteConfirm} color='error' autoFocus>
                  Delete
               </Button>
            </DialogActions>
         </Dialog>
      </Box>
   );
};

export default EventPhotoUploader; 